/* Copyright (c) 2025 Xperi. All Rights Reserved.  */

import { useMemo } from 'react';

import { GuideProgram } from 'client-code/filtered-guide';

import { MILLISECONDS_IN_A_SECOND } from '../../../../../config';
import { AppUtils } from '../../../../../core/AppUtils';
import { Channel } from '../../../../../core/entities';
import { GuideType } from '../../../../../core/enums';
import { ServicesClientImpl } from '../../../../../core/interactors';
import { Locale, LocaleKeys } from '../../../../../core/locale';
import { TChannelV2 } from '../../../../../core/types';
import { DateTime } from '../../../../../core/utils';
import { ChannelCell } from '../../../ChannelCell';
import { ProgramCell } from '../../../ProgramCell';
import { CellTypes } from '../../../ProgramCell/CellTypes';
import { isChannelPartOfFilteredOutChannels } from '../../filteredguide.utils';
import { FilteredGuideHighlight } from '../../FilteredGuideHighlight/FilteredGuideHighlight';

import './FilteredGuideTableRow.scss';

interface IFilteredGuideTableRowProps {
    filteredGuideRow: GuideProgram;
    allPossibleChannels: Array<TChannelV2 | Channel>;
    recordingsStatusMap: Map<string, string>;
    rowIndex?: number;
}

const FilteredGuideTableRow = ({
    filteredGuideRow,
    allPossibleChannels,
    recordingsStatusMap,
    rowIndex,
}: IFilteredGuideTableRowProps) => {
    const services = new ServicesClientImpl();
    const shouldHideMetadata = services.shouldHideMetadata();

    const channel = useMemo(() => {
        const ch = allPossibleChannels.find((c) => c.channelId === filteredGuideRow.channelId);
        if (!ch) {
            return null;
        }

        const isPartOfFilteredGuide = isChannelPartOfFilteredOutChannels(ch);
        if (isPartOfFilteredGuide) {
            return { ...ch, isSubscribed: false };
        }
        return ch;
    }, [allPossibleChannels, filteredGuideRow.channelId]);

    const movieYear = useMemo(() => {
        return filteredGuideRow.contentType === 'movie' ? filteredGuideRow.movieYear : null;
    }, [filteredGuideRow.movieYear, filteredGuideRow.contentType]);

    const recordingStatus = recordingsStatusMap.get(
        `${channel?.stationId}${filteredGuideRow.offerId}`,
    );

    if (!channel) {
        return null;
    }

    return (
        <div className="filtered-guide-table-row-container" data-testid="filtered-guide-table-row">
            <div className="filtered-guide-generic-row">
                <div className="filtered-guide-left-side-table filtered-guide-channel-cell">
                    <ChannelCell
                        channel={channel}
                        highlightId={AppUtils.createHighlightId(0, rowIndex || 0)}
                    >
                        <FilteredGuideHighlight
                            id={AppUtils.createHighlightId(0, rowIndex || 0)}
                        ></FilteredGuideHighlight>
                    </ChannelCell>
                </div>
                <div
                    className="filtered-guide-right-side-table text filtered-guide-show-cell"
                    data-testid="filtered-guide-program-cell"
                >
                    <ProgramCell
                        title={
                            shouldHideMetadata && filteredGuideRow.isAdult
                                ? Locale.getString(LocaleKeys.GUIDE_TITLE_HIDDEN)
                                : filteredGuideRow.title
                        }
                        type={CellTypes.DEFAULT}
                        isSubscribed={channel.isSubscribed}
                        duration={100}
                        movieYear={
                            shouldHideMetadata && filteredGuideRow.isAdult ? null : movieYear
                        }
                        highlightId={AppUtils.createHighlightId(1, rowIndex || 0)}
                        guideType={GuideType.FILTERED_GUIDE}
                        isStartover={filteredGuideRow.isStartover}
                        isCatchup={filteredGuideRow.isCatchup}
                        startTime={new DateTime(filteredGuideRow.startTime * 1000)}
                        endTime={new DateTime(filteredGuideRow.endTime * 1000)}
                        channel={channel}
                        recordingStatus={recordingStatus}
                        isNew={filteredGuideRow.isNew}
                        cuAvailabilityDuration={
                            (channel.policyInfo?.find((policy) => policy?.socuRules)?.socuRules
                                ?.cuAvailabilityDurationSeconds || 0) * MILLISECONDS_IN_A_SECOND
                        }
                    />
                </div>
            </div>
        </div>
    );
};

export default FilteredGuideTableRow;
